import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Save, Check, TrendingUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FIELDS = [
  {
    key: 'investment_knowledge', label: 'Investment Knowledge',
    desc: 'How familiar the client is with investing.',
    options: ['None', 'Limited', 'Good', 'Extensive'],
    guide: {
      None: 'No investing experience (e.g., only a savings account).',
      Limited: 'Knows the basics (e.g., RRSP/TFSA, GICs).',
      Good: 'Understands stocks, bonds and mutual funds.',
      Extensive: 'Actively manages a diversified portfolio.',
    },
  },
  {
    key: 'time_horizon', label: 'Time Horizon',
    desc: 'How long until the client needs this money.',
    options: ['Less than 3 years', '3 to 10 years', '10+ years'],
    guide: {
      'Less than 3 years': 'Short term (e.g., a car or a home down payment).',
      '3 to 10 years': 'Medium term (e.g., a child’s education).',
      '10+ years': 'Long term (e.g., retirement).',
    },
  },
  {
    key: 'risk_tolerance', label: 'Risk Tolerance',
    desc: 'How comfortable the client is with the investment value rising and falling.',
    options: ['Low', 'Medium', 'High'],
    guide: {
      Low: 'Prefers safety; does not want to lose principal (e.g., GICs).',
      Medium: 'Accepts some ups and downs for higher returns (e.g., balanced funds).',
      High: 'Can handle large swings for maximum growth (e.g., equity funds).',
    },
  },
  {
    key: 'investment_objective', label: 'Investment Objective',
    desc: 'What the client mainly wants the money to do.',
    options: ['Safety', 'Income', 'Growth', 'Aggressive Growth'],
    guide: {
      Safety: 'Protect capital above all.',
      Income: 'Generate steady income (e.g., interest/dividends).',
      Growth: 'Grow the money over time.',
      'Aggressive Growth': 'Maximize growth, accepting high risk.',
    },
  },
  {
    key: 'income_range', label: 'Annual Income Range',
    desc: 'Approximate total yearly household income before taxes.',
    options: ['Under $50,000', '$50,000 - $100,000', '$100,000 - $150,000', '$150,000 - $250,000', 'Over $250,000'],
  },
];

export const InvestorProfileForm = ({ caseId, investorProfile, agent, onUpdate }) => {
  const [form, setForm] = useState({
    investment_knowledge: '', time_horizon: '', risk_tolerance: '',
    investment_objective: '', income_range: '', notes: '', ...(investorProfile || {}),
  });
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const dirtyRef = useRef(false);
  const formRef = useRef(form);
  formRef.current = form;

  const set = (k, v) => { dirtyRef.current = true; setForm(prev => ({ ...prev, [k]: v })); };

  const save = useCallback(async (silent = false) => {
    if (!silent) setSaving(true);
    try {
      const r = await fetch(
        `${API_URL}/api/compliance/cases/${caseId}?advisor_id=${agent.id}&advisor_name=${encodeURIComponent(agent.name)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ investor_profile: formRef.current }),
        }
      );
      if (r.ok) {
        dirtyRef.current = false;
        setSavedAt(new Date());
        if (!silent && onUpdate) onUpdate();
      }
    } catch (err) {
      console.error('Failed to save investor profile:', err);
    } finally {
      if (!silent) setSaving(false);
    }
  }, [caseId, agent, onUpdate]);

  // Iter 64 — auto-save every 60s when there are unsaved changes.
  useEffect(() => {
    const id = setInterval(() => { if (dirtyRef.current) save(true); }, 60000);
    return () => clearInterval(id);
  }, [save]);

  return (
    <Card data-testid="investor-profile-form">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-[#0077B6]" /> Investor Profile
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-gray-500">A simple suitability snapshot. Auto-saves every minute.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          {FIELDS.map(f => (
            <div key={f.key}>
              <label className="block text-sm font-medium text-gray-700">{f.label}</label>
              {f.desc && <p className="text-xs text-gray-400 mb-1">{f.desc}</p>}
              <select
                value={form[f.key] || ''}
                onChange={(e) => set(f.key, e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#0077B6]"
                data-testid={`investor-${f.key}`}
              >
                <option value="">Select…</option>
                {f.options.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
              {f.guide && (
                <ul className="mt-1.5 space-y-0.5">
                  {f.options.map(o => (
                    <li key={o} className={`text-[11px] leading-snug ${form[f.key] === o ? 'text-[#0077B6] font-medium' : 'text-gray-400'}`}>
                      <span className="font-semibold">{o}:</span> {f.guide[o]}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
          <textarea
            value={form.notes || ''}
            onChange={(e) => set('notes', e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#0077B6]"
            data-testid="investor-notes"
          />
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={() => save(false)} disabled={saving} className="bg-[#0077B6] hover:bg-[#005A8C]" data-testid="investor-save-btn">
            <Save className="h-4 w-4 mr-1" /> {saving ? 'Saving…' : 'Save'}
          </Button>
          {savedAt && (
            <span className="text-xs text-green-600 flex items-center gap-1">
              <Check className="h-3.5 w-3.5" /> Saved {savedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default InvestorProfileForm;
